<template>
    <v-card class="products-card" elevation="0" dark>
        <header class="card-heading">
            <v-icon small>{{ mdiArchiveOutline }}</v-icon>
            <h2>Registered devices</h2>
        </header>
        <p class="card-description">Find and export registered devices, or select devices to move or delete them.</p>

        <div class="products-toolbar">
            <div class="products-filters">
                <v-text-field
                    v-model="search"
                    label="Search serials"
                    dense
                    hide-details
                    clearable
                    outlined
                    color="#f0784c"
                >
                    <template v-slot:prepend-inner
                        ><v-icon small>{{ mdiMagnify }}</v-icon></template
                    >
                </v-text-field>
                <v-select
                    v-model="selectedModel"
                    outlined
                    dense
                    item-value="model"
                    item-text="model"
                    label="Model"
                    clearable
                    :items="querySMModel"
                    hide-details
                    color="#f0784c"
                    @change="$emit('list-registered', { selectedOrg, selectedModel })"
                >
                    <template v-slot:prepend-inner
                        ><v-icon small>{{ mdiPackage }}</v-icon></template
                    >
                </v-select>
                <v-select
                    v-model="selectedOrg"
                    outlined
                    dense
                    item-value="orgId"
                    item-text="organizationName"
                    label="Organization"
                    clearable
                    hide-details
                    :items="orgList"
                    :loading="loading"
                    color="#f0784c"
                    @change="$emit('list-registered', { selectedOrg, selectedModel })"
                >
                    <template v-slot:prepend-inner
                        ><v-icon small>{{ mdiAccountSupervisorCircle }}</v-icon></template
                    >
                </v-select>
            </div>
            <div class="products-actions">
                <v-btn class="secondary-action" outlined @click="$emit('export-current')">
                    <v-icon small left>{{ mdiDownload }}</v-icon> Export
                </v-btn>
                <v-menu v-if="userIsOrgAdmin" offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            class="primary-action"
                            color="#db5120"
                            elevation="0"
                            :disabled="!selectedSNs.length || bulkSubmitLoading"
                            v-bind="attrs"
                            v-on="on"
                        >
                            Selected devices <v-icon small right>{{ mdiChevronDown }}</v-icon>
                        </v-btn>
                    </template>
                    <v-list dark dense>
                        <v-list-item @click="openBulkAction('move')">
                            <v-list-item-title>Move selected</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openBulkAction('delete')">
                            <v-list-item-title>Delete selected</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-dialog
                    v-model="dialog"
                    max-width="640"
                    :fullscreen="$vuetify.breakpoint.xsOnly"
                    :persistent="moveSubmitLoading"
                    scrollable
                >
                    <!-- The card owns its scoped theme, even when Vuetify detaches the dialog. -->
                    <v-card class="move-dialog-card" dark elevation="0">
                        <header class="move-dialog-header">
                            <h2>Move devices</h2>
                            <v-btn
                                icon
                                small
                                aria-label="Close move devices dialog"
                                :disabled="moveSubmitLoading"
                                @click="dialog = false"
                            >
                                <v-icon small>{{ mdiClose }}</v-icon>
                            </v-btn>
                        </header>
                        <div class="move-dialog-body">
                            <p class="move-summary"
                                >{{ selectedSNs.length }} device(s) selected. All devices must belong to the same source
                                organization.</p
                            >
                            <div class="move-fields">
                                <div class="source-organization-field">
                                    <v-select
                                        v-model="fromOrg"
                                        :items="orgList"
                                        item-text="organizationName"
                                        item-value="orgId"
                                        outlined
                                        dense
                                        hide-details
                                        readonly
                                        label="Source organization"
                                        color="#f0784c"
                                        :error="hasMixedSourceOrganizations"
                                        :aria-describedby="
                                            hasMixedSourceOrganizations ? 'move-source-warning' : undefined
                                        "
                                    >
                                        <template v-slot:prepend-inner
                                            ><v-icon small>{{ mdiOfficeBuilding }}</v-icon></template
                                        >
                                    </v-select>
                                    <p
                                        v-if="hasMixedSourceOrganizations"
                                        id="move-source-warning"
                                        class="move-source-warning"
                                        role="alert"
                                    >
                                        <v-icon small aria-hidden="true">{{ mdiAlertCircleOutline }}</v-icon>
                                        The selected devices belong to different organizations. Select devices from the
                                        same source organization to move them.
                                    </p>
                                </div>
                                <v-select
                                    v-model="toOrg"
                                    :items="orgList"
                                    item-text="organizationName"
                                    item-value="orgId"
                                    outlined
                                    dense
                                    hide-details
                                    clearable
                                    label="Destination organization"
                                    color="#f0784c"
                                    :disabled="moveSubmitLoading"
                                >
                                    <template v-slot:prepend-inner
                                        ><v-icon small>{{ mdiOfficeBuilding }}</v-icon></template
                                    >
                                </v-select>
                            </div>
                            <p v-if="moveValidationMessage" class="move-validation" role="status">{{
                                moveValidationMessage
                            }}</p>
                            <section v-if="selectedSNs.length" class="move-selection" aria-label="Selected devices">
                                <h3>Selected serial numbers</h3>
                                <v-data-table
                                    class="move-table"
                                    dark
                                    dense
                                    disable-sort
                                    :mobile-breakpoint="0"
                                    :headers="serialHeaders"
                                    :items="selectedSNs"
                                    :items-per-page="-1"
                                    item-key="_tableKey"
                                    hide-default-footer
                                />
                            </section>
                        </div>
                        <footer class="move-dialog-actions">
                            <v-btn
                                class="secondary-action"
                                outlined
                                :disabled="moveSubmitLoading"
                                @click="dialog = false"
                                >Cancel</v-btn
                            >
                            <v-btn
                                class="primary-action"
                                color="#db5120"
                                elevation="0"
                                :disabled="!validSNs || moveSubmitLoading"
                                :loading="moveSubmitLoading"
                                @click="submitMoveSerial"
                            >
                                <v-icon small left>{{ mdiArrowRightBoldCircle }}</v-icon> Complete move
                            </v-btn>
                        </footer>
                    </v-card>
                </v-dialog>
                <v-dialog
                    v-model="deleteDialog"
                    max-width="560"
                    :persistent="deleteSubmitLoading"
                    :fullscreen="$vuetify.breakpoint.xsOnly"
                    scrollable
                >
                    <v-card class="move-dialog-card" dark elevation="0">
                        <header class="move-dialog-header">
                            <h2>Delete selected devices?</h2>
                            <v-btn
                                icon
                                small
                                aria-label="Close delete devices dialog"
                                :disabled="deleteSubmitLoading"
                                @click="deleteDialog = false"
                            >
                                <v-icon small>{{ mdiClose }}</v-icon>
                            </v-btn>
                        </header>
                        <div class="move-dialog-body">
                            <p class="move-summary">
                                This will remove {{ selectedSNs.length }} selected device(s) from product registration.
                                This action cannot be undone.
                            </p>
                            <v-data-table
                                class="move-table"
                                dark
                                dense
                                disable-sort
                                :mobile-breakpoint="0"
                                :headers="serialHeaders"
                                :items="selectedSNs"
                                :items-per-page="-1"
                                item-key="_tableKey"
                                hide-default-footer
                            />
                        </div>
                        <footer class="move-dialog-actions">
                            <v-btn
                                class="secondary-action"
                                outlined
                                :disabled="deleteSubmitLoading"
                                @click="deleteDialog = false"
                                >Cancel</v-btn
                            >
                            <v-btn
                                class="primary-action"
                                color="#db5120"
                                elevation="0"
                                :disabled="!selectedSNs.length || deleteSubmitLoading"
                                :loading="deleteSubmitLoading"
                                @click="submitDeleteSerials"
                                >Delete selected</v-btn
                            >
                        </footer>
                    </v-card>
                </v-dialog>
            </div>
        </div>

        <div v-if="userIsOrgAdmin" class="selection-toolbar">
            <span role="status" aria-live="polite">{{ selectedSNs.length }} device(s) selected</span>
            <button
                v-if="selectedSNs.length"
                type="button"
                class="clear-selection"
                :disabled="bulkSubmitLoading"
                @click="clearSelection"
            >
                Clear selection
            </button>
            <span v-else class="selection-hint">Use the checkboxes to choose devices to move or delete.</span>
        </div>

        <v-data-table
            class="products-table"
            dark
            dense
            :headers="tableHeaders"
            :search="search"
            disable-sort
            :no-data-text="noItemsMessage"
            :items="formattedSns"
            item-key="_tableKey"
            :item-class="rowClass"
            :loading="loading"
            :mobile-breakpoint="0"
        >
            <template v-slot:item.selection="{ item }">
                <input
                    type="checkbox"
                    class="row-select"
                    :checked="isSelected(item)"
                    :aria-label="(isSelected(item) ? 'Deselect ' : 'Select ') + item.sn"
                    :disabled="bulkSubmitLoading"
                    @click.stop
                    @change="toggleSelection(item)"
                />
            </template>
            <template v-slot:item.registerDate="{ item }">{{ new Date(item.registerDate).toLocaleString() }}</template>
            <template v-slot:no-data>
                <div class="products-empty">
                    <v-icon small>{{ mdiArchiveOutline }}</v-icon>
                    <strong>No registered devices found</strong>
                    <span>Adjust your filters or register a device above.</span>
                </div>
            </template>
            <template v-slot:no-results>
                <div class="products-empty"
                    ><strong>No matching devices</strong
                    ><span>Try a different serial number or clear your search.</span></div
                >
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import {
    mdiArchiveOutline,
    mdiMagnify,
    mdiPackage,
    mdiAccountSupervisorCircle,
    mdiDownload,
    mdiArrowRightBoldCircle,
    mdiOfficeBuilding,
    mdiClose,
    mdiAlertCircleOutline,
    mdiChevronDown
} from '@mdi/js'

export default {
    name: 'ListRegisteredProductsTab',
    props: {
        orgList: { type: Array, default: () => [] },
        querySMModel: { type: Array, default: () => [] },
        registeredSns: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        userIsOrgAdmin: { type: Boolean, required: true }
    },
    data() {
        return {
            mdiArchiveOutline,
            mdiMagnify,
            mdiPackage,
            mdiAccountSupervisorCircle,
            mdiDownload,
            mdiArrowRightBoldCircle,
            mdiOfficeBuilding,
            mdiClose,
            mdiAlertCircleOutline,
            mdiChevronDown,
            search: '',
            selectedModel: '',
            selectedOrg: '',
            selectedSNs: [],
            dialog: false,
            deleteDialog: false,
            deleteSubmitLoading: false,
            fromOrg: '',
            toOrg: '',
            moveSubmitLoading: false,
            noItemsMessage: 'No registered serials found.',
            serialHeaders: [{ text: 'Selected Serial #', value: 'sn', align: 'left' }],
            optionHeaders: [
                { text: 'Serial #', value: 'sn', align: 'left' },
                { text: 'Model', value: 'model' },
                { text: 'Organization', value: 'orgName' },
                { text: 'Asset Tag', value: 'assetTag' },
                { text: 'Location', value: 'location', align: 'left' },
                { text: 'In Warranty', value: 'inwarranty' },
                { text: 'Register Date', value: 'registerDate' }
            ]
        }
    },
    computed: {
        bulkSubmitLoading() {
            return this.moveSubmitLoading || this.deleteSubmitLoading
        },
        tableHeaders() {
            return this.userIsOrgAdmin
                ? [{ text: 'Select', value: 'selection', filterable: false, width: 64 }, ...this.optionHeaders]
                : this.optionHeaders
        },
        formattedSns() {
            return this.registeredSns.map((item, i) => ({ ...item, _tableKey: item.sn + '_' + i }))
        },
        selectionSourceOrg() {
            const first = this.selectedSNs[0]
            return first && first.orgId && this.selectedSNs.every((item) => item.orgId === first.orgId)
                ? first.orgId
                : ''
        },
        hasMixedSourceOrganizations() {
            return this.selectedSNs.length > 1 && !this.selectionSourceOrg
        },
        validSNs() {
            return (
                this.userIsOrgAdmin &&
                !!this.selectionSourceOrg &&
                this.fromOrg === this.selectionSourceOrg &&
                !!this.toOrg &&
                this.toOrg !== this.fromOrg &&
                this.orgList.some((org) => org.orgId === this.fromOrg) &&
                this.orgList.some((org) => org.orgId === this.toOrg)
            )
        },
        moveValidationMessage() {
            if (!this.selectedSNs.length) return 'Select at least one device to move.'
            if (this.hasMixedSourceOrganizations) return ''
            if (!this.selectionSourceOrg) return 'Select devices from a single source organization.'
            if (!this.orgList.some((org) => org.orgId === this.selectionSourceOrg))
                return 'The source organization is unavailable.'
            if (!this.toOrg) return 'Choose a destination organization.'
            if (this.toOrg === this.selectionSourceOrg) return 'Destination must differ from the source organization.'
            if (!this.orgList.some((org) => org.orgId === this.toOrg))
                return 'The destination organization is unavailable.'
            return ''
        }
    },
    watch: {
        registeredSns: {
            deep: true,
            handler() {
                // Refetched/reordered rows must never retain keys or organization data from an old list.
                this.clearSelection()
            }
        },
        selectedSNs: {
            deep: true,
            handler() {
                this.isValid()
            }
        }
    },
    methods: {
        openBulkAction(action) {
            if (!this.userIsOrgAdmin || !this.selectedSNs.length || this.bulkSubmitLoading) return
            if (action === 'delete') {
                this.deleteDialog = true
            } else if (action === 'move') {
                this.dialog = true
                this.$emit('get-orgs')
            }
        },
        submitDeleteSerials() {
            if (!this.userIsOrgAdmin || !this.deleteDialog || !this.selectedSNs.length || this.bulkSubmitLoading) return
            this.deleteSubmitLoading = true
            this.$emit('delete-serials', {
                selectedSNs: [...this.selectedSNs],
                onComplete: this.handleDeleteSerialsComplete
            })
        },
        handleDeleteSerialsComplete(success, failedDevices = []) {
            this.deleteSubmitLoading = false
            if (success) {
                this.deleteDialog = false
                this.clearSelection()
            } else if (failedDevices.length) {
                this.selectedSNs = this.formattedSns.filter((item) =>
                    failedDevices.some((failed) => failed.sn === item.sn && failed.orgId === item.orgId)
                )
            }
        },
        isSelected(item) {
            return this.selectedSNs.some((selected) => selected._tableKey === item._tableKey)
        },
        rowClass(item) {
            return this.isSelected(item) ? 'selected-device-row' : ''
        },
        toggleSelection(item) {
            if (!this.userIsOrgAdmin || this.bulkSubmitLoading) return
            this.selectedSNs = this.isSelected(item)
                ? this.selectedSNs.filter((selected) => selected._tableKey !== item._tableKey)
                : [...this.selectedSNs, item]
            this.isValid()
        },
        clearSelection() {
            if (!this.bulkSubmitLoading) {
                this.dialog = false
                this.deleteDialog = false
            }
            this.selectedSNs = []
            this.fromOrg = ''
            this.toOrg = ''
        },
        isValid() {
            // v-select uses item-value="orgId": keep the source value a string, not an organization object.
            this.fromOrg = this.selectionSourceOrg
        },
        submitMoveSerial() {
            this.isValid()
            if (!this.validSNs || this.bulkSubmitLoading) return
            this.moveSubmitLoading = true
            this.$emit('move-serial', {
                selectedSNs: this.selectedSNs,
                fromOrg: this.fromOrg,
                toOrg: this.toOrg,
                onComplete: this.handleMoveSerialComplete
            })
        },
        handleMoveSerialComplete(success) {
            this.moveSubmitLoading = false
            if (success) {
                this.dialog = false
                this.clearSelection()
            }
        }
    }
}
</script>

<style scoped>
.products-card,
.move-dialog-card {
    min-width: 0;
    border: 1px solid #3b3e47;
    border-radius: 12px !important;
    background: #282b33 !important;
    color: #f3f4f6;
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.products-card {
    width: 100%;
    padding: 20px;
}
.card-heading {
    display: flex;
    align-items: center;
    gap: 8px;
}
.card-heading .v-icon {
    color: #f0784c;
}
.card-heading h2,
.move-dialog-header h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.3px;
}
.card-description,
.move-summary {
    margin: 8px 0 16px;
    color: #b8bbc5;
    line-height: 1.6;
}
.products-toolbar {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.products-filters {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    flex: 1;
    min-width: 0;
}
.products-actions,
.move-dialog-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.products-actions .v-btn {
    height: 40px;
}
.products-card ::v-deep .v-btn,
.move-dialog-card ::v-deep .v-btn {
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
.secondary-action {
    color: #f0784c !important;
}
.secondary-action {
    border-color: #6e493c;
}
.primary-action:hover:not(:disabled) {
    background-color: #f0784c !important;
}
.products-card ::v-deep .v-input,
.products-card ::v-deep .v-label,
.move-dialog-card ::v-deep .v-input,
.move-dialog-card ::v-deep .v-label {
    font-family: inherit;
    font-size: 12px;
}
.products-card ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot,
.move-dialog-card ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: 40px;
    background: #22252d;
    border-radius: 8px;
}
.products-card ::v-deep .v-text-field--outlined:not(.v-input--is-focused) fieldset,
.move-dialog-card ::v-deep .v-text-field--outlined:not(.v-input--is-focused) fieldset {
    border-color: #484c57;
}
.products-card ::v-deep .v-input .v-icon,
.move-dialog-card ::v-deep .v-input .v-icon {
    color: #b8bbc5;
}
.selection-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin: 14px 0;
    min-height: 32px;
    color: #e2e4e9;
}
.selection-hint {
    color: #b8bbc5;
}
.clear-selection {
    padding: 6px 8px;
    border-radius: 6px;
    color: #f0784c;
    text-decoration: underline;
}
.row-select {
    width: 18px;
    height: 18px;
    vertical-align: middle;
    accent-color: #f0784c;
    cursor: pointer;
}
.clear-selection:hover:not(:disabled) {
    background: #493229;
}
.row-select:disabled,
.clear-selection:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.row-select:focus-visible,
.clear-selection:focus-visible,
.products-card ::v-deep .v-btn:focus-visible,
.move-dialog-card ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 2px;
}
.products-table,
.move-table {
    margin-top: 14px;
    background: #22252d !important;
    border: 1px solid #3b3e47;
    border-radius: 8px;
}
.products-table ::v-deep .v-data-table__wrapper,
.move-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}
.products-table ::v-deep table {
    min-width: 1080px;
}
.products-table ::v-deep th,
.products-table ::v-deep td,
.move-table ::v-deep th,
.move-table ::v-deep td {
    font-size: 12px !important;
    white-space: nowrap;
    border-bottom-color: #3b3e47 !important;
}
.products-table ::v-deep th,
.move-table ::v-deep th {
    color: #b8bbc5 !important;
    background: #22252d;
}
.products-table ::v-deep tbody tr:hover,
.move-table ::v-deep tbody tr:hover {
    background: #30343e !important;
}
.products-table ::v-deep tbody tr.selected-device-row,
.products-table ::v-deep tbody tr.selected-device-row:hover {
    background: #3c302c !important;
}
.products-table ::v-deep .v-data-footer {
    border-top-color: #3b3e47;
    font-size: 12px;
}
.products-empty {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 24px 0;
    text-align: left;
    color: #b8bbc5;
    white-space: normal;
}
.products-empty strong {
    color: #e2e4e9;
}
.products-empty .v-icon {
    color: #f0784c;
}
.move-dialog-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.move-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid #3b3e47;
}
.move-dialog-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 20px;
    overflow-y: auto;
}
.move-summary {
    margin-top: 0;
}
.move-fields {
    display: grid;
    gap: 12px;
}
.source-organization-field {
    min-width: 0;
}
.move-source-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 8px 0 0;
    padding: 10px 12px;
    border: 1px solid #a45c58;
    border-radius: 7px;
    color: #ffb4ac;
    background: rgba(164, 92, 88, 0.12);
    font-size: 12px;
    line-height: 1.5;
}
.move-source-warning .v-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: #ffb4ac;
}
.move-validation {
    margin: 12px 0 0;
    color: #f6b69c;
    line-height: 1.6;
}
.move-selection {
    margin-top: 20px;
}
.move-selection h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
}
.move-table ::v-deep .v-data-table__wrapper {
    max-height: 220px;
    overflow-y: auto;
}
.move-dialog-actions {
    justify-content: flex-end;
    padding: 16px 20px;
    border-top: 1px solid #3b3e47;
}
@media (max-width: 1100px) {
    .products-toolbar {
        flex-direction: column;
    }
    .products-filters {
        width: 100%;
    }
    .products-actions {
        align-self: flex-end;
    }
}
@media (max-width: 600px) {
    .products-card {
        padding: 16px;
    }
    .products-filters {
        grid-template-columns: 1fr;
    }
    .products-actions {
        width: 100%;
    }
    .products-actions .v-btn {
        flex: 1;
    }
    .move-dialog-card {
        height: 100%;
        max-height: 100%;
        border-radius: 0 !important;
    }
    .move-dialog-header,
    .move-dialog-body,
    .move-dialog-actions {
        padding: 16px;
    }
    .move-dialog-actions .v-btn {
        flex: 1;
    }
}
</style>
